import React from "react";

import {
  AppsSection,
  CreateMenuPopover,
  HomeFavoritesSection,
  HomeNavSection,
  MyWorkflowsSection,
  ModuleMenu,
  type ModuleMenuSection,
} from "#app/components/navigation/module-menu";
import { useAppData } from "#app/lib/data";
import { useFavoriteIssues } from "#app/lib/hooks/use-favorite-issues";
import { useExternalAppLinks } from "#app/lib/hooks/use-external-app-links";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useCanCreateProject } from "#app/lib/hooks/use-can-create-project";
import { useIssueView } from "#app/components/issue-view";
import { useCloseSidebarIfCompact } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";
import { useModuleRail } from "#app/components/navigation/module-rail/module-rail-context";
import { type HomeSectionId } from "./home-sections";

// id → Component for the user-orderable sections. Typed by `HomeSectionId`
// so adding a section to the registry without a component here is a type
// error (and vice versa the registry stays the single id source).
const SECTION_COMPONENTS: Record<HomeSectionId, React.ComponentType> = {
  favorites: HomeFavoritesSection,
  "my-workflows": MyWorkflowsSection,
  apps: AppsSection,
};

export function HomeModuleMenu() {
  const { workflows } = useAppData();
  // Mirror-backed rows, so the Favorites slot is decided on the shell's first
  // frame rather than popping in after the preferences fetch.
  const { sidebarRows: favoriteIssues } = useFavoriteIssues();
  const externalAppLinks = useExternalAppLinks();
  const { openCreateIssue } = useIssueView();
  const canCreateWorkflow = usePermission("create.workflows");
  // Drives where we land after creating: the workflow settings editor is
  // gated on manage.edit_workflows, so a create-only user must go to the
  // board instead (mirrors the workflow landing page).
  const canCreateProject = useCanCreateProject();
  const closeDrawer = useCloseSidebarIfCompact();
  const { homeSectionOrder } = useModuleRail();
  const { openCreateWorkflow, openNewDashboard } = useNavDialogActions();

  // The fixed nav section is always first; the rest follow the user's
  // configured order. A section is skipped while it has nothing to show —
  // the conditional applies AFTER ordering so it keeps its slot otherwise.
  // Favorites: skip when empty. My Workflows: skip when there are no visible
  // workflows AND the viewer can't create one (an empty, actionless accordion).
  const sections = React.useMemo<ModuleMenuSection[]>(() => {
    const list: ModuleMenuSection[] = [
      { id: "nav", Component: HomeNavSection },
    ];
    for (const id of homeSectionOrder) {
      if (id === "favorites" && favoriteIssues.length === 0) continue;
      if (
        id === "my-workflows" &&
        workflows.length === 0 &&
        !canCreateWorkflow
      )
        continue;
      // Apps: skip when the workspace has no external links (an empty,
      // actionless section otherwise renders a divider + blank header).
      if (id === "apps" && externalAppLinks.length === 0) continue;
      list.push({ id, Component: SECTION_COMPONENTS[id] });
    }
    return list;
  }, [homeSectionOrder, favoriteIssues.length, workflows.length, canCreateWorkflow, externalAppLinks.length]);

  // A permission-gated action is undefined without its grant, and the popover
  // drops a row whose action is absent.
  const onCreateProject = React.useMemo(
    () =>
      canCreateProject
        ? () => {
            closeDrawer();
            openCreateIssue({ source: "home_menu" });
          }
        : undefined,
    [canCreateProject, closeDrawer, openCreateIssue],
  );
  const onCreateWorkflow = React.useMemo(
    () =>
      canCreateWorkflow
        ? () => {
            closeDrawer();
            openCreateWorkflow();
          }
        : undefined,
    [canCreateWorkflow, closeDrawer, openCreateWorkflow],
  );
  // Anyone may create a dashboard: the row is private to its creator until
  // shared.
  const onCreateDashboard = React.useCallback(() => {
    closeDrawer();
    openNewDashboard();
  }, [closeDrawer, openNewDashboard]);
  return (
    <>
      <ModuleMenu
        title="Home"
        sections={sections}
        createMenu={
          <CreateMenuPopover
            ariaLabel="Home actions"
            customizeInitialTab="home"
            onCreateProject={onCreateProject}
            onCreateWorkflow={onCreateWorkflow}
            onCreateDashboard={onCreateDashboard}
          />
        }
        // Pinned footer opening Customize Navigation on the Home tab — the
        // module menu's own sections are what that tab reorders.
        customizeNav={{ initialTab: "home" }}
      />
    </>
  );
}
